import { Action } from '@compass/client/types/case_record';
import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';

import { caseRecordService } from '@/services/api';
import { useStore } from '@/store/index';

// Hook to fire the informational manual-task-started lifecycle marker (hermes
// PR #820 / BI-913). Fired when a user starts working a manual task: input edit
// on missing-info, output edit on human-audit, dialer open on human-call. The
// backend derives the user from the auth context and forwards the signal to the
// case's workflow.
export function useManualTaskStarted(caseId: string) {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  const startedMutation = useMutation({
    mutationFn: () => {
      if (!selectedOrganizationId || !selectedProgramId || !caseId) {
        throw new Error('Organization, program, and case context required');
      }
      return caseRecordService.executeAction({
        organization_id: selectedOrganizationId,
        program_id: selectedProgramId,
        case_record_id: caseId,
        action: Action.ACTION_MANUAL_TASK_STARTED,
      });
    },
    onError: () => {
      // Informational marker only — a delivery failure must never interrupt the
      // user's edit/call. Swallow silently.
    },
  });

  /** Fire the manual-task-started marker (fire-and-forget). */
  const notifyManualTaskStarted = useCallback(() => {
    startedMutation.mutateAsync().catch(() => {
      // Handled by onError above.
    });
  }, [startedMutation]);

  return { notifyManualTaskStarted };
}
